/**
 * Cloud text calls for the writers' room.
 * The home-computer text job is not sent from here until its queue body
 * has been copied from api.py. This file does not open port 11434.
 */

/** Groq free chat model. Llama 3.1 on Groq is not on the free tier. */
export const GROQ_MODEL = 'openai/gpt-oss-20b';

/** Gemini JSON model. One constant so a renamed model is a one-line change. */
export const GEMINI_MODEL = 'gemini-2.5-flash';

const TEXT_TIMEOUT_MS = 30_000;

export type ProviderFailure = 'limit' | 'down' | 'bad-json';

/** A text call failed in a way the room can route. */
export class ProviderError extends Error {
  readonly failure: ProviderFailure;

  constructor(failure: ProviderFailure, message: string) {
    super(message);
    this.name = 'ProviderError';
    this.failure = failure;
  }
}

/**
 * The Ollama queue body is not in this repo. Calling it would mean guessing
 * the JSON api.py accepts.
 */
export class QueueBodyUnconfirmed extends Error {
  constructor() {
    super('The home computer text job is not confirmed yet.');
    this.name = 'QueueBodyUnconfirmed';
  }
}

/** 429 and quota text are limits. Everything else that is not OK is an outage. */
export function classifyHttpFailure(status: number, body: string): ProviderFailure {
  if (status === 429) return 'limit';
  if (/quota|rate limit/i.test(body)) return 'limit';
  return 'down';
}

async function postJson(
  url: string,
  body: unknown,
  apiKey?: string
): Promise<{ status: number; body: string }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TEXT_TIMEOUT_MS);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const text = await response.text();
    return { status: response.status, body: text };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    throw new ProviderError('down', message);
  } finally {
    clearTimeout(timer);
  }
}

function requireOk(status: number, body: string): Record<string, unknown> {
  if (status < 200 || status >= 300) {
    throw new ProviderError(classifyHttpFailure(status, body), `Text request failed (${status})`);
  }
  try {
    const value: unknown = JSON.parse(body);
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new ProviderError('bad-json', 'Text reply was not JSON');
    }
    return value as Record<string, unknown>;
  } catch (err) {
    if (err instanceof ProviderError) throw err;
    throw new ProviderError('bad-json', 'Text reply was not JSON');
  }
}

function groqText(payload: Record<string, unknown>): string {
  const choices = payload.choices;
  if (!Array.isArray(choices) || !choices[0] || typeof choices[0] !== 'object') return '';
  const message = (choices[0] as { message?: { content?: unknown } }).message;
  const content = message?.content;
  return typeof content === 'string' ? content.trim() : '';
}

function geminiText(payload: Record<string, unknown>): string {
  const candidates = payload.candidates;
  if (!Array.isArray(candidates) || !candidates[0] || typeof candidates[0] !== 'object') return '';
  const content = (candidates[0] as { content?: { parts?: unknown } }).content;
  const parts = content?.parts;
  if (!Array.isArray(parts)) return '';
  return parts
    .map((part) => {
      if (!part || typeof part !== 'object') return '';
      const text = (part as { text?: unknown }).text;
      return typeof text === 'string' ? text : '';
    })
    .join('')
    .trim();
}

/**
 * One Groq chat completion. JSON mode is requested so the reply is an object.
 * Returns the message text. The room parses that text.
 */
export async function groqComplete(apiKey: string, system: string, user: string): Promise<string> {
  const result = await postJson(
    'https://api.groq.com/openai/v1/chat/completions',
    {
      model: GROQ_MODEL,
      temperature: 0.4,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: system },
        { role: 'user', content: user },
      ],
    },
    apiKey
  );
  const text = groqText(requireOk(result.status, result.body));
  if (!text) throw new ProviderError('bad-json', 'Text reply was empty');
  return text;
}

/**
 * One Gemini generateContent call. The key is a query parameter, which is
 * how that API takes it. responseMimeType asks for JSON.
 */
export async function geminiComplete(apiKey: string, system: string, user: string): Promise<string> {
  const url =
    `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent` +
    `?key=${encodeURIComponent(apiKey)}`;
  const result = await postJson(url, {
    systemInstruction: { parts: [{ text: system }] },
    contents: [{ role: 'user', parts: [{ text: user }] }],
    generationConfig: { responseMimeType: 'application/json', temperature: 0.4 },
  });
  const text = geminiText(requireOk(result.status, result.body));
  if (!text) throw new ProviderError('bad-json', 'Text reply was empty');
  return text;
}

/**
 * Desktop Ollama through the queue. The request body is not confirmed, so
 * this rejects instead of posting a guessed payload.
 */
export function ollamaComplete(): Promise<string> {
  return Promise.reject(new QueueBodyUnconfirmed());
}
